/**
 * The kitchen is only concerned with a few pieces of info about an Order (item names, qty, fields).
 * This is a smaller Order model that only contains that required info.
 */
const FulfillmentMethodEnum = require('@/lib/Orders/FulfillmentMethodEnum');
const { cloneShallow } = require('@/functions/clone');
const { populate } = require('@/functions/objects');
const { toHMS } = require('@/functions/date');

class KitchenOrder {
    constructor(data = {}) {
        /**
         * The time the order was first sent to the kitchen.
         *
         * @type {?Date}
         */
        this.fulfillmentCreatedAt = null;

        /**
         * The time the order was completed by the kitchen ("Order Away" pressed).
         *
         * @type {?Date}
         */
        this.fulfilledAt = null;

        /**
         * @type {OrderFulfillment[]}
         */
        this.fulfillments = [];

        /**
         * The date the order was last modified (if applicable).
         *
         * @type {?Date}
         */
        this.modifiedAt = null;

        /**
         * @type {KitchenOrderItem[]}
         */
        this.items = [];

        /**
         * Several things may affect which items "remain" in the order, such as
         * items being fulfilled, or items only being visible in certain zones.
         * This array will hold the original list of items before those filters
         * are applied.
         *
         * If setFulfillments has never been called this will be null.
         * If it has been called, this will be the contents of `this.items` at the
         * time of the first call.
         *
         * @type {KitchenOrderItem[]|null}
         */
        this.itemsBeforeFulfillments = null;

        /**
         * @type {?string}
         */
        this.notes = null;

        /**
         * @type {?number}
         */
        this.orderID = null;

        /**
         * The order's bookingRef.
         *
         * @type {?string}
         */
        this.orderRef = null;

        /**
         * How the customer receives this order.
         *
         * @type {OrderFulfillmentInstructions|null}
         */
        this.fulfillmentInstructions = null;

        /**
         * The reference used consistently for this order throughout the kitchen.
         * In order of preference, it is:
         * - Order staffRef (should contain the table number)
         * - Order collection number (if present)
         * - Order bookingRef (gotta have something!)
         *
         * The table name and collection number are available separately via fulfillmentInstructions.
         *
         * @type {?string}
         */
        this.kitchenRef = null;

        /**
         * @type {?string}
         */
        this.thirdPartyRef = null;

        /**
         * @type {boolean}
         */
        this.cancelled = false;

        /**
         * @type {string}
         */
        this.deviceName = null;

        /**
         * @type {User|null}
         */
        this.seller = null;

        /**
         * The payment channel the order was made through.
         *
         * @type {PaymentChannel|null}
         */
        this.paymentChannel = null;

        /**
         * The name of the zone or zones that should be printed on the receipt.
         *
         * @type {string|null}
         */
        this.receiptZoneName = null;

        /**
         * An array that contains each kitchenZoneID in the order.
         *
         * @type {number[]|null}
         */
        this.kitchenZoneIDsInOrder = null;

        populate(this, data);
    }

    /**
     * @param {KitchenOrderItem} item
     */
    addItem(item) {
        this.items.push(item);
    }

    getCreatedAt() {
        return this.fulfillmentCreatedAt || new Date();
    }

    isModified() {
        return !!(this.modifiedAt && this.fulfillmentCreatedAt
            && this.modifiedAt > this.fulfillmentCreatedAt
            && toHMS(this.modifiedAt) !== toHMS(this.fulfillmentCreatedAt));
    }

    /**
     * @returns {boolean}
     */
    isProMenuOrder() {
        return !!this.paymentChannel?.isProMenu();
    }

    /**
     * @returns {boolean}
     */
    isTableService() {
        return this.fulfillmentInstructions?.fulfillmentMethod === FulfillmentMethodEnum.TABLE_SERVICE;
    }

    /**
     * @returns {boolean}
     */
    isCollection() {
        return this.fulfillmentInstructions?.fulfillmentMethod === FulfillmentMethodEnum.COLLECTION;
    }

    /**
     * @returns {KitchenOrderItem[]}
     */
    getVisibleItems() {
        // merge
        // return this.items.filter((item) => item.isVisible !== false);
        return this.items.filter((item) => item.isVisible);
    }

    /**
     * Add up the qty of all the items (lines) in the order.
     * Used in OrderFulfillmentService.markAsFulfilled()
     * Todo: Should this filter by _versionIndex?
     *
     * @return {number}
     */
    get totalItemQty() {
        return this.items
            .reduce((total, item) => total + item.qty, 0);
    }

    /**
     * Add up the qty of all the visible items (lines) in the order.
     *
     * @returns {number}
     */
    get totalVisibleItemQty() {
        return this.getVisibleItems()
            .reduce((total, item) => total + item.qty, 0);
    }

    /**
     * Add up the qty of all the visible items (lines) in the order.
     *
     * @returns {number}
     */
    get totalVisibleUnfulfilledItemQty() {
        return this.getVisibleItems()
            .reduce((total, item) => total + item.qtyUnfulfilled, 0);
    }

    /**
     * Add up the qty of all the visible items (lines) in the order.
     *
     * @returns {number}
     */
    get totalVisibleFulfilledItemQty() {
        return this.getVisibleItems()
            .filter((item) => item._versionIndex === 0)
            .reduce((total, item) => total + item.qtyFulfilled, 0);
    }

    /**
     * Add up the qty of all the fulfilled items (lines) in the current version of the order.
     *
     * @returns {number}
     */
    get unfulfilledInvisibleItemQty() {
        return this.items
            .filter((item) => !item.isVisible && item._versionIndex === 0)
            .reduce((total, item) => total + item.qtyUnfulfilled, 0);
    }

    /**
     * Add up the qty of all the fulfilled items (lines) in the current version of the order.
     *
     * @returns {number}
     */
    get totalFulfilledItemQty() {
        return this.items
            .filter((item) => item._versionIndex === 0)
            .reduce((total, item) => total + item.qtyFulfilled, 0);
    }

    /**
     * Todo: Check if we can remove this getter.
     *       Note: as of PROP-1719 this is only used in KitchenOrderTest.js and KitchenOrderFactoryTest.js
     *       totalItemQty does not take into account _versionIndex
     *
     * @returns {number}
     */
    get totalUnfulfilledItemQty() {
        return this.totalItemQty - this.totalFulfilledItemQty;
    }

    /**
     * We now use an array of fulfillments to adjust the fulfilled qty of items in the order.
     * Each call to this will reset the items to their original state and then apply the new
     * fulfilled qts to each item.
     *
     * @param {OrderFulfillment[]} fulfillments
     */
    setFulfillments(fulfillments) {
        if (this.itemsBeforeFulfillments === null) {
            // Backup the original items.
            this.itemsBeforeFulfillments = cloneShallow(this.items);
        }

        this.fulfillments = fulfillments;

        const fulfilledItems = fulfillments.reduce(
            (acc, fulfillment) => acc.concat(fulfillment.fulfilledItems), []
        );

        this.items = this.itemsBeforeFulfillments
            .map((item) => {
                const newItem = cloneShallow(item);

                // Reduce the quantity for any items already fulfilled.
                const matchingFulfilledItems = fulfilledItems.filter(
                    (fulfilledItem) => fulfilledItem.hash === newItem.hash
                );

                newItem.qtyFulfilled = matchingFulfilledItems.reduce(
                    (carry, fulfilledItem) => carry + parseInt(fulfilledItem.qty),
                    0
                );

                return newItem;
            })
            .filter((item) => item.qty > 0);
    }
}

module.exports = KitchenOrder;
